import {useState, useEffect} from 'react'
import axios from 'axios'
import {browserName, osName, isMobile, isTablet} from 'react-device-detect'

type DeviceInfo = {
  deviceId: string
  ipAddress: string
  latitude: number | null
  longitude: number | null
  deviceType: string
  browser: string
  loginTimestamp: string
}

export const useDeviceInfo = () => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    deviceId: '',
    ipAddress: '',
    latitude: 0,
    longitude: 0,
    deviceType: '',
    browser: '',
    loginTimestamp: '',
  })

  useEffect(() => {
  const fetchIpAddress = async () => {
    const currentTimestamp = new Date().toISOString();
    let ipAddress = '-';
    let deviceType = '-';
    let browser = '-';
    let latitude = 0;
    let longitude = 0;
    
    // Label browser logins so Insights can distinguish them from native mobile app
    // (app sends "iOS - Mobile App" / "Android - Mobile App")
    const formFactor = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'
    deviceType = `${osName || formFactor} - Browser`
    browser = browserName;

    try {
      // 1. Fetch IP Address
      const response = await axios.get('https://api.ipify.org?format=json');
      ipAddress = response.data.ip;

      // 2. Attempt to get Location
      try {
        const location = await getLocation();
        latitude = location?.latitude ?? 0;
        longitude = location?.longitude ?? 0;
      } catch (locationError) {
        console.error('Error fetching location (using dummy data):', locationError);
        // latitude and longitude remain 0 on failure
      }

      // Final success state update (includes dummy location on location failure)
      setDeviceInfo({
        deviceId: ipAddress,
        ipAddress: ipAddress,
        latitude: latitude,
        longitude: longitude,
        deviceType: deviceType,
        browser: browser,
        loginTimestamp: currentTimestamp,
      });

    } catch (error) {
      // 3. Handle IP fetch failure (axios.get fails)
      console.error('Error fetching IP address (using dummy data):', error);
      
      // Update state with dummy data for all fields
      setDeviceInfo({
        deviceId: '-', // Dummy
        ipAddress: '-', // Dummy
        latitude: 0, // Dummy
        longitude: 0, // Dummy
        deviceType: deviceType, // Use derived info if available
        browser: browser, // Use derived info if available
        loginTimestamp: currentTimestamp, // Use current time
      });
    }
  };

  fetchIpAddress();
}, [isMobile, isTablet, osName, browserName]); // Added dependencies for completeness


  const getLocation = async (): Promise<{latitude: number; longitude: number} | null> => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const {latitude, longitude} = position.coords
            resolve({latitude, longitude})
          },
          (error) => {
            console.error('Error getting location:', error)
            resolve(null)
          }
        )
      } else {
        console.error('Geolocation is not supported by this browser.')
        resolve(null)
      }
    })
  }

  return deviceInfo
}
